import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocationApi } from '../../location-select/hooks/use-location-api';
import { BlankModal, HStack, ModalBody, ModalFooter, ModalHeader, ModalProps } from '@cad-arc';
import { FormBody } from '../../forms/integrated-form-components/form-body';
import { IntegratedFormProvider } from '../../forms/integrated-form-provider';
import { useFullscreenModalControls } from '../../modals/context/modal-context-provider';
import { useCopyCallApi } from '../hooks/use-copy-call-api';
import { CopyCallForm, CopyCallFormDisplayType } from './copy-call-form';
import { FormCancelButton } from '../../forms/integrated-form-components/form-cancel-button';
import { FormSaveButton } from '../../forms/integrated-form-components/form-save-button';
import { ConstrainedModalProps } from '../../modals/types';
import { useOpenEventPanel } from '../../event-panel/use-open-event-panel';
import { AgencyEvent } from '@mark43/cad-api';

export type CopyCallModalComponentType = typeof CopyCallModal;

export type CopyCallModalProps = {
    agencyEventId: number;
    portalProps?: ModalProps['portalProps'];
} & ConstrainedModalProps;

export const CopyCallModal: React.FC<CopyCallModalProps> = ({
    agencyEventId,
    portalProps,
    closeOnOverlayClick,
    constrainToParentPortal,
    onClose,
}) => {
    const { payForEsriLocation } = useLocationApi();
    const modalControls = useFullscreenModalControls();

    const finalOnClose = onClose ?? modalControls.close;
    const openEventPanel = useOpenEventPanel();

    const onCancel = React.useCallback(
        function onCancel(): void {
            finalOnClose();
        },
        [finalOnClose]
    );

    const onSuccess = React.useCallback(
        function onSuccess(resp: AgencyEvent): void {
            finalOnClose();
            openEventPanel({
                eventId: resp.id,
            });
        },
        [finalOnClose, openEventPanel]
    );

    const { copyCallFormData, copyCall, query, mutation } = useCopyCallApi(
        agencyEventId,
        onSuccess
    );

    const form = useForm<CopyCallFormDisplayType>({ mode: 'onBlur' });

    function onSubmit(formShape: CopyCallFormDisplayType): void {
        if (form.getFieldState('consolidatedLocationView').isDirty) {
            payForEsriLocation(form.getValues('consolidatedLocationView')!);
        }
        copyCall(formShape);
    }

    return (
        <IntegratedFormProvider
            query={query}
            mutation={mutation}
            form={form}
            onSubmit={onSubmit}
            inputSize="sm"
        >
            <BlankModal
                portalProps={portalProps}
                size="md"
                onClose={onCancel}
                isOpen={true}
                className="copy-call-modal"
                constrainToPortal={constrainToParentPortal}
                closeOnOverlayClick={closeOnOverlayClick}
                style={constrainToParentPortal ? { maxHeight: '80%' } : undefined}
                scrollBehavior={constrainToParentPortal ? 'inside' : undefined}
            >
                <ModalHeader>Copy Call</ModalHeader>
                <ModalBody>
                    <FormBody>
                        <FormProvider {...form}>
                            {copyCallFormData && (
                                <CopyCallForm formData={copyCallFormData} form={form} />
                            )}
                        </FormProvider>
                    </FormBody>
                </ModalBody>
                <ModalFooter>
                    <HStack w="100%" justifyContent="flex-end" spacing={2}>
                        <FormCancelButton onClick={onCancel} />
                        <FormSaveButton>Create New Event</FormSaveButton>
                    </HStack>
                </ModalFooter>
            </BlankModal>
        </IntegratedFormProvider>
    );
};
